<template>
  <div class="ui-showcase">
    <ds-page-header title="UI 组件库展示" subtitle="设计 Token 与公共组件的视觉参考页，仅用于开发自检，不含业务数据">
      <template slot="extra">
        <el-button size="small" icon="el-icon-refresh" @click="reload">重新加载</el-button>
      </template>
    </ds-page-header>

    <!-- 1. Token 色板 -->
    <section class="ui-showcase__section">
      <h3 class="ui-showcase__section-title">设计 Token</h3>
      <div class="ui-showcase__card">
        <h4>品牌 / 语义色</h4>
        <div class="ui-showcase__swatches">
          <div v-for="item in brandSwatches" :key="item.name" class="ui-showcase__swatch">
            <div class="ui-showcase__swatch-color" :style="{ background: item.value }"></div>
            <div class="ui-showcase__swatch-name">{{ item.name }}</div>
            <div class="ui-showcase__swatch-value">{{ item.value }}</div>
          </div>
        </div>

        <h4>中性色阶</h4>
        <div class="ui-showcase__swatches">
          <div v-for="item in graySwatches" :key="item.name" class="ui-showcase__swatch">
            <div class="ui-showcase__swatch-color" :style="{ background: item.value }"></div>
            <div class="ui-showcase__swatch-name">{{ item.name }}</div>
            <div class="ui-showcase__swatch-value">{{ item.value }}</div>
          </div>
        </div>

        <h4>图表色板</h4>
        <div class="ui-showcase__swatches">
          <div v-for="(value, key) in chartSwatches" :key="key" class="ui-showcase__swatch">
            <div class="ui-showcase__swatch-color" :style="{ background: value }"></div>
            <div class="ui-showcase__swatch-name">chart-{{ key }}</div>
            <div class="ui-showcase__swatch-value">{{ value }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- 2. 状态标签 -->
    <section class="ui-showcase__section">
      <h3 class="ui-showcase__section-title">状态标签 DsStatusTag</h3>
      <div class="ui-showcase__card">
        <el-row :gutter="12">
          <el-col :span="4" v-for="t in statusTypes" :key="t">
            <ds-status-tag :type="t" :label="statusLabel[t]"></ds-status-tag>
          </el-col>
        </el-row>
        <p class="ui-showcase__tip">状态必须「圆点 + 文字」，颜色仅作辅助（WCAG 1.4.1）</p>
      </div>
    </section>

    <!-- 3. KPI 卡 -->
    <section class="ui-showcase__section">
      <h3 class="ui-showcase__section-title">指标卡 DsKpiCard</h3>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6" v-for="k in kpis" :key="k.label">
          <ds-kpi-card
            :label="k.label"
            :value="k.value"
            :unit="k.unit"
            :tone="k.tone"
            :trend="k.trend"
            trend-label="较上月">
          </ds-kpi-card>
        </el-col>
      </el-row>
    </section>

    <!-- 4. 空态 / 错误态 -->
    <section class="ui-showcase__section">
      <h3 class="ui-showcase__section-title">空态与错误态</h3>
      <el-row :gutter="16">
        <el-col :xs="24" :md="12">
          <div class="ui-showcase__card ui-showcase__card--flat">
            <ds-empty-state type="no-data" description="调整筛选条件后重试">
              <template slot="action">
                <el-button size="small">重置筛选</el-button>
              </template>
            </ds-empty-state>
          </div>
        </el-col>
        <el-col :xs="24" :md="12">
          <div class="ui-showcase__card ui-showcase__card--flat">
            <ds-error-state description="网络异常，请稍后重试" code="500" @retry="onRetry"></ds-error-state>
          </div>
        </el-col>
      </el-row>
    </section>

    <!-- 5. 图表 -->
    <section class="ui-showcase__section">
      <h3 class="ui-showcase__section-title">图表 DsReportChart</h3>
      <el-row :gutter="16">
        <el-col :xs="24" :md="12">
          <ds-chart-panel title="趋势示例" subtitle="基于演示数据" :summary="chartSummary">
            <ds-report-chart :option="lineOption" height="300px"></ds-report-chart>
          </ds-chart-panel>
        </el-col>
        <el-col :xs="24" :md="12">
          <ds-chart-panel title="构成示例" :summary="pieSummary">
            <ds-report-chart :option="pieOption" height="300px"></ds-report-chart>
          </ds-chart-panel>
        </el-col>
      </el-row>
    </section>

    <!-- 6. 列表骨架 -->
    <section class="ui-showcase__section">
      <h3 class="ui-showcase__section-title">列表骨架（查询区 + 工具条 + 表格 + 分页）</h3>
      <ds-search-panel>
        <el-row :gutter="16">
          <el-col :span="6">
            <el-input placeholder="小区名称" v-model="showcase.keyword"></el-input>
          </el-col>
          <el-col :span="6">
            <el-select placeholder="费用类型" v-model="showcase.feeType">
              <el-option label="物业费" value="1"></el-option>
              <el-option label="水费" value="2"></el-option>
            </el-select>
          </el-col>
          <el-col :span="6">
            <el-date-picker v-model="showcase.date" type="date" placeholder="收费日期"></el-date-picker>
          </el-col>
        </el-row>
        <template slot="actions">
          <el-button type="primary" icon="el-icon-search" @click="onSearch">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="onReset">重置</el-button>
        </template>
      </ds-search-panel>

      <ds-toolbar :total="tableData.length" :columns="tableColumns" @refresh="loadTable">
        <template slot="right">
          <el-button size="mini" type="primary" icon="el-icon-plus">新增</el-button>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" border>
        <el-table-column prop="name" label="项目" width="180" />
        <el-table-column prop="amount" label="金额" width="140" align="right">
          <template slot-scope="scope">
            <span class="ds-amount">{{ money(scope.row.amount) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="state" label="状态" width="110">
          <template slot-scope="scope">
            <ds-status-tag :type="scope.row.type" :label="scope.row.state"></ds-status-tag>
          </template>
        </el-table-column>
        <el-table-column prop="date" label="日期" width="130" />
        <el-table-column label="操作" width="140" fixed="right">
          <template>
            <el-button type="text" size="mini">查看</el-button>
            <el-button type="text" size="mini">编辑</el-button>
            <el-button type="text" size="mini" class="text-danger">删除</el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="tableData.length" :page="1" :size="10" summary="本页合计 ¥12,345.00"></ds-pagination-bar>
    </section>
  </div>
</template>

<script>
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsKpiCard from '@/components/ds/ds-kpi-card.vue'
import DsChartPanel from '@/components/ds/ds-chart-panel.vue'
import DsReportChart from '@/components/ds/ds-report-chart.vue'
import DsEmptyState from '@/components/ds/ds-empty-state.vue'
import DsErrorState from '@/components/ds/ds-error-state.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import { formatMoney } from '@/utils/format'

export default {
  name: 'UiShowcaseList',
  components: {
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsKpiCard,
    DsChartPanel,
    DsReportChart,
    DsEmptyState,
    DsErrorState,
    DsPaginationBar
  },
  data () {
    return {
      showcase: {
        keyword: '',
        feeType: '',
        date: ''
      },
      brandSwatches: [
        { name: 'primary-50', value: '#EEF2FF' },
        { name: 'primary-100', value: '#DCE5FF' },
        { name: 'primary-200', value: '#B9CFFF' },
        { name: 'primary-500', value: '#3B6DE8' },
        { name: 'primary-600', value: '#2456D6' },
        { name: 'primary-700', value: '#1A41A8' },
        { name: 'primary-900', value: '#0E2A5C' },
        { name: 'success', value: '#12B76A' },
        { name: 'warning', value: '#F79009' },
        { name: 'danger', value: '#F04438' },
        { name: 'info', value: '#0BA5EC' },
        { name: 'nav-bg', value: '#0E2A5C' }
      ],
      graySwatches: [
        { name: 'gray-50', value: '#F8FAFC' },
        { name: 'gray-100', value: '#F1F5F9' },
        { name: 'gray-200', value: '#E8EDF3' },
        { name: 'gray-300', value: '#D6DEE8' },
        { name: 'gray-400', value: '#9AA8BC' },
        { name: 'gray-500', value: '#6B7B92' },
        { name: 'gray-600', value: '#4A5A70' },
        { name: 'gray-700', value: '#334155' },
        { name: 'gray-800', value: '#1E293B' },
        { name: 'gray-900', value: '#0F172A' }
      ],
      chartSwatches: {
        1: '#2456D6',
        2: '#12B3A8',
        3: '#7A5AF8',
        4: '#F79009',
        5: '#F04438',
        6: '#0BA5EC'
      },
      statusTypes: ['success', 'warning', 'danger', 'info', 'primary', 'muted'],
      statusLabel: {
        success: '已完成',
        warning: '待处理',
        danger: '已逾期',
        info: '草稿',
        primary: '处理中',
        muted: '已取消'
      },
      kpis: [
        { label: '实收总额', value: 1286540.5, unit: '元', tone: 'primary', trend: 8.2 },
        { label: '收缴率', value: 96.35, unit: '%', tone: 'success', trend: 1.4 },
        { label: '欠费总额', value: 53210.8, unit: '元', tone: 'danger', trend: -3.6 },
        { label: '在管房屋', value: 1284, unit: '户', tone: 'info', trend: 0 }
      ],
      tableColumns: [
        { prop: 'name', label: '项目' },
        { prop: 'amount', label: '金额' },
        { prop: 'state', label: '状态' },
        { prop: 'date', label: '日期' }
      ],
      tableData: [
        { name: '物业费 2026-03', amount: 3200.5, state: '已完成', type: 'success', date: '2026-03-05' },
        { name: '水费 2026-02', amount: 128.2, state: '待缴费', type: 'warning', date: '2026-02-28' },
        { name: '停车费 2026-03', amount: 640, state: '已逾期', type: 'danger', date: '2026-03-01' }
      ],
      lineOption: {
        xAxis: { type: 'category', data: ['1月', '2月', '3月', '4月', '5月', '6月'] },
        yAxis: { type: 'value' },
        series: [
          { type: 'line', name: '实收', smooth: true, data: [820, 932, 901, 1290, 1330, 1520] },
          { type: 'line', name: '欠费', smooth: true, data: [320, 302, 341, 290, 330, 210] }
        ]
      },
      pieOption: {
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        series: [
          {
            type: 'pie',
            radius: ['45%', '68%'],
            label: { formatter: '{b}\n{d}%' },
            data: [
              { value: 1048, name: '物业费' },
              { value: 735, name: '水费' },
              { value: 580, name: '电费' },
              { value: 484, name: '停车费' }
            ]
          }
        ]
      }
    }
  },
  computed: {
    chartSummary () {
      return '实收金额自 1 月至 6 月整体上升，欠费金额在 5 月后回落。'
    },
    pieSummary () {
      return '收款方式构成中，物业费占比最高。'
    }
  },
  methods: {
    money (val) {
      return formatMoney(val)
    },
    loadTable () {
      this.$message.success('演示数据，无需重新加载')
    },
    reload () {
      this.$router.go(0)
    },
    onSearch () {
      this.$message.info('演示页：查询逻辑不接入真实接口')
    },
    onReset () {
      this.showcase.keyword = ''
      this.showcase.feeType = ''
      this.showcase.date = ''
    },
    onRetry () {
      this.$message.success('已触发重试')
    }
  }
}
</script>

<style lang="scss" scoped>
.ui-showcase {
  padding: var(--ds-spacing-lg);
  background: var(--ds-gray-100);
  min-height: calc(100vh - 60px);

  &__section {
    margin-bottom: var(--ds-spacing-xl);
  }

  &__section-title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
    margin: 0 0 var(--ds-spacing-md);
  }

  &__card {
    background: #fff;
    border: 1px solid var(--ds-gray-300);
    border-radius: var(--ds-radius-md);
    box-shadow: var(--ds-shadow-sm);
    padding: var(--ds-spacing-lg);

    &--flat {
      padding: 0;
    }

    h4 {
      font-size: var(--ds-font-size-sm);
      font-weight: 600;
      color: var(--ds-gray-600);
      margin: 0 0 var(--ds-spacing-sm);
    }
  }

  &__swatches {
    display: flex;
    flex-wrap: wrap;
    gap: var(--ds-spacing-md);
    margin-bottom: var(--ds-spacing-lg);
  }

  &__swatch {
    width: 120px;

    &-color {
      height: 56px;
      border-radius: var(--ds-radius-sm);
      border: 1px solid var(--ds-gray-200);
    }

    &-name {
      margin-top: var(--ds-spacing-xs);
      font-size: var(--ds-font-size-xs);
      color: var(--ds-gray-700);
      font-weight: 600;
    }

    &-value {
      font-size: 11px;
      color: var(--ds-gray-500);
      font-family: var(--ds-font-family-num);
    }
  }

  &__tip {
    margin: var(--ds-spacing-md) 0 0;
    font-size: var(--ds-font-size-xs);
    color: var(--ds-gray-500);
  }
}
</style>
